import { Injectable } from '@angular/core';

import {
  each,
  extend,
  filter,
  find,
  findIndex,
  get,
  isArray,
  isString,
  remove,
  sortBy,
  isEmpty
} from 'lodash';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { TestListEntity } from './test-list.model.factory';
import { Device } from '../../providers/device/device';
import { first, takeUntil } from 'rxjs/operators';
import { IAgentSelector, ITestList, RunTypeValue, TestTypeValue } from './types';
import { IEntity, IEntityCache, TimeMode } from '../../providers/inputs.datastore/types';
import { ApiService } from '../../providers/api/api.service';
import { DevicesService } from '../../providers/device/devices.service';
import { LoggerService } from '../../providers/logger/logger.service';
import { InputDatastoreService } from '../../providers/inputs.datastore/inputs.datastore.service';
import { BenchmarkService } from '../benchmark/benchmark.service';
import { UnmanagedDevicesService } from '../../providers/unmanaged-devices/unmanaged-devices.service';
import { NotificationService } from '../../components/notifications/notifications.service';
import { TimestampRange } from '../../components/time-picker/types';
import { NavigationProvider } from '@nrwl/navigation-abstract';

@Injectable({
  providedIn: 'root'
})
export class TestListService implements IEntityCache {
  deltaTimeFrom: number;
  realtimetestcache: Array<TestListEntity> = [];
  cache: BehaviorSubject<Array<TestListEntity>>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  siteList: any;
  item: TestListEntity;
  pageSize = 5000; // consecutive query page size
  updated: Subject<Array<TestListEntity>>;
  queryLoading = false;
  overallTests: Array<TestListEntity> = [];
  initialQuery = false;
  dataLoading: BehaviorSubject<boolean> = new BehaviorSubject(true);
  isApiExecuted = false;
  filterTypes = [
    'twamp-initiator',
    'twamp-reflector',
    'twamp-light-reflector',
    'y1731-initiator',
    'y1564',
    'bert',
    'rfc2544',
    'truespeed',
    'udp',
    'voip',
    'http',
    'ftp',
    'servicecontinuity',
    'http',
    'multistep',
    'bert'
  ];
  private readonly unsubscribe: Subject<void> = new Subject();
  private GetSingleTestURL = '/sbi/restconf/data/v2/list';
  private addTestURL = '/uicomm/StartTest';
  private startTestURL = '/sbi/restconf/data/v2/bulk/ietf-lmap-control:lmap?create=false';
  private stopTestURL = '/sbi/restconf/operations/v2/action/bulk/lmap';
  private stopSingleTestURL = '/uicomm/StopTest';
  private startSingleTestURL = '/uicomm/ExecTest';
  private deleteTestURL = '/sbi/restconf/data/deleteTest';
  private getTestStatusURL = '/sbi/restconf/data/status?scheduleid';
  private getTestConfigURL = '/sbi/restconf/data/config?scheduleid';
  private getTwampBtfFlowExportURL = '/sbi/restconf/data/v2/export/config?scheduleid';

  constructor(
    private readonly apiService: ApiService,
    private readonly devicesService: DevicesService,
    private readonly logging: LoggerService,
    private readonly inputDatastoreService: InputDatastoreService,
    private readonly benchmarkService: BenchmarkService,
    private readonly unmanagedDeviceService: UnmanagedDevicesService,
    private readonly notificationService: NotificationService,
    private readonly navigation: NavigationProvider
  ) {
    this.deltaTimeFrom = null;
    this.realtimetestcache = [];

    this.cache = new BehaviorSubject([]); //new BehaviorSubject<Array<TestListEntity>>([]);
  }

  getEntityName(): string {
    return 'testlist';
  }

  findById(id: string): IEntity {
    return this.getTestListById(id);
  }

  findByName(name: string): IEntity {
    return find(this.getTestLists(), testlist => testlist.getName() === name);
  }

  getByName(name: string): IEntity[] {
    return filter(this.getTestLists(), testlist => testlist.getName() === name);
  }

  findByEscapedName(name: string): IEntity {
    return find(this.getTestLists(), testlist => testlist.getEscapedName() === name);
  }

  getByEscapedName(name: string): IEntity[] {
    return filter(this.getTestLists(), testlist => testlist.getEscapedName() === name);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValues(): any[] {
    return this.getTestLists();
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValuesAsync(): Observable<Array<any>> {
    /*eslint-enable*/
    const tests = this.getValues();
    if (!tests || tests.length === 0) {
      return this.updateTestLists(this.realtimetestcache).pipe(first());
    }
    return of(tests).pipe(first());
  }

  getTestLists(): Array<TestListEntity> {
    return this.chooseCacheModel();
  }

  getTestListById(testId: string): TestListEntity {
    return find(this.getTestLists(), testlist => testlist.getId() == testId);
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.dataLoading.next(true);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTestListById$(testId: string): any {
    const url = `${this.GetSingleTestURL}/` + testId;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.get$<any>(url, {});
  }

  updateTestLists(
    cache: Array<TestListEntity>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params?: TimestampRange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    firstRequest?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mode?: TimeMode,
    action?: string,
    scrollId = null
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    if (this.queryLoading) {
      return this.updated;
    }
    this.updated = new Subject();
    this.benchmarkService.registerPageEvent('testQuery');
    let paginateURL = `${this.GetSingleTestURL}?scheduleid=all&type=${this.filterTypes}&size=${this.pageSize}&useScroll=true`;
    if (scrollId) {
      paginateURL = paginateURL + `&scrollId=` + scrollId;
    }
    this.queryLoading = true;
    this.initialQuery = false;
    if (!this.isApiExecuted && cache.length === 0) {
      this.dataLoading.next(true);
    }
    this.apiService
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get$<any>(paginateURL, {})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.queryLoading = false;
          this.isApiExecuted = true;
          this.benchmarkService.logPageEventTime('Testlist query', 'testQuery');
          this.benchmarkService.registerPageEvent('dataManipulation');
          if (response?.message === 'success' && isArray(response.schedules)) {
            if (!scrollId) {
              // First api call clear the tests array
              this.overallTests = [];
            }
            if (cache.length === 0) {
              this.initialQuery = true;
            }
            const responseData = this.transformResponse(response.schedules);
            this.overallTests.push(...responseData);
            this.assignTestToCacheList(cache);
            this.assignSiteAndAgent(cache);
            this.updated.next(this.getTestLists());
            this.benchmarkService.logPageEventTime(
              'Testlist transforming data',
              'dataManipulation'
            );
            this.dataLoading.next(false);
            this.paginateTests(response.stat.found, response.stat.scrollId);
          }
        },
        // eslint-disable-next-line
        error => {
          this.isApiExecuted = true;
          this.queryLoading = false;
          this.dataLoading.next(false);
        },
        () => {
          this.isApiExecuted = true;
          this.updated.complete();
          this.queryLoading = false;
          this.dataLoading.next(false);
        }
      );
    return this.updated;
  }

  paginateTests(count: number, scrollId: string): void {
    if (count === this.pageSize) {
      if (this.initialQuery) {
        //Update the cache immediately when first response came back
        this.cache.next(this.chooseCacheModel());
      }
      this.updateTestLists(this.getTestLists(), null, null, null, null, scrollId);
    } else {
      //update the cache once all scroll api completed
      this.cache.next(this.chooseCacheModel());
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  transformResponse(dataIn: any): Array<TestListEntity> {
    dataIn = isString(dataIn) ? JSON.parse(dataIn) : dataIn;
    const response = [];
    if (dataIn !== null && isArray(dataIn)) {
      dataIn.forEach(result => {
        if (result.primary && typeof result.primary === 'object') {
          result.primary.topId = result.id;
          result.primary['secondary'] = result?.secondary;
          result.primary.status = result.status;
          result = result.primary;
        }

        result.runType = this.getRunType(result);
        const configIn = <ITestList>{};
        extend(configIn, result);
        const testObj: TestListEntity = new TestListEntity(configIn);
        response.push(testObj);
      });
    }
    return response;
  }

  createTestListData(testLists: TestListEntity): any {
    const testListData = { tpInfo: [] };
    if (testLists.getOriginatorAgentId()) {
      testListData.tpInfo.push({
        type: 'primary',
        id: testLists.getOriginatorAgent().getUniqueId(),
        ip: testLists.getOriginatorAgent().getIpAddress()
      });
    }
    if (testLists.getDestinationAgentId()) {
      testListData.tpInfo.push({
        type: 'primary',
        id: testLists.getDestinationAgent().getUniqueId(),
        ip: testLists.getDestinationAgent().getIpAddress()
      });
    }
    return testListData;
  }

  //eslint-disable-next-line
  getRunType(result): string {
    const runType =
      typeof result['test-list-data'] === 'string' && result['test-list-data'] !== 'none'
        ? JSON.parse(result['test-list-data'])?.['runType']
          ? JSON.parse(result['test-list-data'])['runType']
          : null
        : null;
    if (runType) {
      return runType;
    } else {
      if (
        result.type === TestTypeValue.TWAMP_LIGHT_REFLECTOR ||
        result.type === TestTypeValue.TWAMP_REFLECTOR
      ) {
        return RunTypeValue.ON_GOING;
      }
      if (
        result.type === TestTypeValue.TRUESPEED ||
        result.type === TestTypeValue.CONTAINER_TRUESPEED
      ) {
        return RunTypeValue.ON_DEMAND;
      }
    }
    return RunTypeValue.ON_DEMAND;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  async assignSiteAndAgent(cache: any): Promise<void> {
    let deviceList = [];
    await this.devicesService
      .getValuesAsync()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        const unamangedDeviceList = this._getUnManagedDevice$();
        deviceList = response.concat(unamangedDeviceList);
        const deviceItems = this.mapIdWithObject(deviceList);
        for (const testlist of cache) {
          this.assignAgent(deviceItems, testlist);
        }
      });
  }

  //eslint-disable-next-line
  mapIdWithObject(listItem: any): any {
    const mappedObject = {};
    for (const item of listItem) {
      mappedObject[item.getUniqueName() + '-' + item.getPhyId()] = item;
    }
    return mappedObject;
  }

  //eslint-disable-next-line
  assignAgent(deviceItems: any, testLists: TestListEntity): void {
    const originator: Device =
      deviceItems[testLists.getOriginatorAgentAlias() + '-' + testLists.getPhyId()];
    const destination: Device =
      deviceItems[testLists.getDestinationAgentAlias() + '-' + testLists.getDestinationPhyId()];
    if (originator) {
      testLists.setOriginatorAgent(new Device(originator.getDevice()));
    }
    if (destination) {
      testLists.setDestinationAgent(new Device(destination.getDevice()));
    }
    if (isEmpty(testLists.getTestListData())) {
      this.createTestListData(testLists);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getTypeById(typeList: any, id: any): any {
    for (const item of typeList) {
      if (item.getId() === id) {
        return item;
      }
    }
  }

  chooseCacheModel(mode?: TimeMode): Array<TestListEntity> {
    return this.realtimetestcache;
  }

  assignTestToCacheList(cache: Array<TestListEntity>): void {
    // simplified algo, that simply replaces full cache with new list
    // notice we preserve the array object!
    cache.length = 0;
    for (const entry of this.overallTests) {
      cache.push(entry);
    }
  }

  findIndexById(cache: Array<TestListEntity>, uid: string): number {
    return findIndex(cache, test => uid === test.getId());
  }

  findTestByID(testId: string): TestListEntity {
    for (const test of this.getTestLists()) {
      if (test.getId() === testId) {
        return test;
      }
    }
  }

  findTestCacheByID(cache: Array<TestListEntity>, testId: string): TestListEntity {
    for (const test of cache) {
      if (test.getId() === testId) {
        return test;
      }
    }
  }

  /**
   * Remove testlist from cache
   * @param cache
   * @param testId
   * @returns {Array} of Tests that left after removing
   */
  removeTestCacheByID(cache: Array<TestListEntity>, testId: string): Array<TestListEntity> {
    return remove(cache, test => test.getId() === testId);
  }

  findTestByIndex(cache: Array<TestListEntity>, index: number): TestListEntity {
    return get(cache, `[${index}]`, null);
  }

  clearRealtimeCache(): void {
    this.realtimetestcache.length = 0;
  }

  /* eslint-disable-next-line */
  saveControllerTest$(testConfig: any): Observable<any> {
    const url = `${this.addTestURL}`;
    /* eslint-disable-next-line */
    return this.apiService.post$<any>(url, testConfig);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  startTest$(data): Observable<any> {
    const url = `${this.startTestURL}`;
    return this.apiService.post$<any>(url, data);
  }

  startSingleTest$(testId: string): Observable<any> {
    const url = `${this.startSingleTestURL}/${testId}`;
    return this.apiService.post$<any>(url);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeTest$(data: any): Observable<any> {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.delete$<any>(this.deleteTestURL, data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  stopTest$(data): Observable<any> {
    const url = `${this.stopTestURL}`;
    return this.apiService.post$<any>(url, data);
  }

  stopSingleTest$(testId: string): Observable<any> {
    const url = `${this.stopSingleTestURL}/${testId}`;
    return this.apiService.post$<any>(url);
  }

  getTestStatus(scheduledId: string): Observable<any> {
    const url = `${this.getTestStatusURL}=${scheduledId}`;
    return this.apiService.get$<any>(url);
  }

  getTestConfig(scheduledId: string): Observable<any> {
    const url = `${this.getTestConfigURL}=${scheduledId}`;
    return this.apiService.get$<any>(url);
  }

  getBtfFlowExport(scheduledId: string): Observable<any> {
    const url = `${this.getTwampBtfFlowExportURL}=${scheduledId}`;
    return this.apiService.get$<any>(url);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeTestsResponse(response: any, data: any): void {
    const result: Array<string> = response.success;
    if (response.status === '0') {
      this.removeTests(data);
    } else {
      this.logging.info(`Test has return unknown result with params  ${response}`);
    }
  }

  removeTests(data: Array<string>): void {
    const allTests = this.getTestLists();
    each(data, id => {
      this.removeTestCacheByID(allTests, id);
    });

    this.cache.next(this.chooseCacheModel());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateTestStopResponse(response: any, data: any): void {
    const result = response?.details;
    if (result.failed === 0) {
      const newResult = data.map(id => {
        return {
          id: id,
          status: 'stopping',
          msg: 'stopping the Test',
          administrativeState: 'Disabled'
        };
      });
      this.updateTestCacheAfterAction(newResult);
      this.notificationService.onMonitorStopStateChange(newResult);
    } else {
      const failureMessage = response?.message;
      if (failureMessage) {
        this.notificationService.onFail(failureMessage);
      }
      this.logging.info(`Test has return unknown result with params ${data}`);
    }
  }

  getWarningMessage(testInfo: { maxLimit: number; activeCount: number }): string {
    return `There are ${testInfo.activeCount} tests currently enabled. Adding new tests will exceed the ${testInfo.maxLimit} maximum tests limit. Please stop or remove some tests to allow new tests to run.`;
  }

  getCreateTestWarningMsg(limit: number): {
    id: string;
    alternateMsg: string;
    alternateTitle: string;
  } {
    const param = {
      id: 'start.testlist',
      alternateMsg: `You have reached ${limit} tests which is the maximum number of test that can be created. Please stop or remove some tests to allow new tests to run.`,
      alternateTitle: 'Test count limit exceeded'
    };
    return param;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateTestStartResponse(response: any, data: any): void {
    this.logging.info(`Test was started: ${JSON.stringify(response)}`);
    const result = response?.details;
    if (result.failed === 0) {
      const newResult = data.map(id => {
        return {
          id: id,
          status: 'starting',
          msg: 'Starting the Test',
          administrativeState: 'Disabled'
        };
      });
      this.updateTestCacheAfterAction(newResult);
      this.notificationService.onMonitorStartStateChange(newResult);
    } else {
      if (result?.failed === 1) {
        const failureReason = result?.['failed_tests']?.[0]?.['reason'];
        this.notificationService.onFail('Test submission failed. ' + failureReason);
      } else if (result.failed > 1) {
        const failureMessage = response?.message;
        if (failureMessage) {
          this.notificationService.onFail(failureMessage);
        }
      }
      this.logging.info(`Test has return unknown result with params ${data}`);
    }
  }

  updateTestCacheAfterAction(data: Array<any>): void {
    this.updateTestCache(this.getTestLists(), data);
  }

  updateTestCache(cache: Array<TestListEntity>, data: Array<any>): void {
    each(data, item => {
      const index = this.findIndexById(cache, item.id);
      if (index !== -1) {
        this.setTestParameters(cache, item, index);
      }
    });
    this.cache.next(this.chooseCacheModel());
  }

  setTestParameters(cache: Array<TestListEntity>, item: ITestList, index: number): void {
    if (item.status) {
      this.findTestByIndex(cache, index).setTestStatus(item.status);
      this.findTestByIndex(cache, index).setTestStateMessage(item.msg);
      this.findTestByIndex(cache, index).setAdministrativeState(item.administrativeState);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  sortDataByName(data: any): any {
    return sortBy(data, test => test.getName()?.toLowerCase().trim());
  }

  // Clear each page widget component queue
  clearQueues(): void {
    this.inputDatastoreService.clearQueues();
  }

  updateTestResponse(): void {
    this.updateTestLists(this.getTestLists());
  }

  updateDataSourceTestItem(testItem: TestListEntity): void {
    this.item = testItem;
  }

  editTest(testListEnity: TestListEntity, mode = 'edit'): void {
    const defaultParams = {
      modeType: mode,
      scheduleId: testListEnity?.getId()
    };
    let pageId = '';
    switch (testListEnity.getTestType()) {
      case TestTypeValue.TRUESPEED:
        pageId = 'truespeed_test';
        break;
      case TestTypeValue.Y1564:
        pageId = 'y1564_test';
        break;
      case TestTypeValue.UDP:
        pageId = 'udp_test';
        break;
      case TestTypeValue.RFC2544:
        pageId = 'rfc2544_test';
        break;
      case TestTypeValue.SERVICE_CONTINUITY:
        pageId = 'service_test';
        break;
      case TestTypeValue.MULTISTEP:
        pageId = 'multistep_test';
        break;
      case TestTypeValue.VOIP:
        pageId = 'voip_test';
        break;
      case TestTypeValue.TWAMP:
        pageId = 'twamp_test';
        break;
      case TestTypeValue.Y1731:
        pageId = 'y1731_test';
        break;
      case TestTypeValue.TWAMP_LIGHT_REFLECTOR:
      case TestTypeValue.TWAMP_REFLECTOR:
        pageId = 'twamp_reflector_test';
        break;
      case TestTypeValue.HTTP:
        pageId = 'http_test';
        break;
      case TestTypeValue.FTP:
        pageId = 'ftp_test';
        break;
      case TestTypeValue.BERT:
        pageId = 'bert_test';
        break;
    }
    this.navigation.navigateToTestSetup(pageId, {
      queryParams: {
        defaultParams: JSON.stringify(defaultParams)
      }
    });
  }

  formatTwampEditData(mData: ITestList): any {
    let twampRefletors = [];
    let twampLightRefletors = [];
    const testConfigData = mData.config;
    const testConfig = testConfigData.config[0].primaryConfig['ietf-lmap-control:lmap'];
    const pmInterval = testConfig.events.event[0].periodic.interval;
    const testdata = testConfig.schedules.schedule[0].action[0].option;
    const vlan = this.getTwampOriginatorInfo(testdata, 'vlan-id');
    const cvlan = this.getTwampOriginatorInfo(testdata, 'inner-vlan-id');
    let twampReflector = this.getTwampOriginatorInfo(testdata, 'twamp');
    let twampReflectorLight = this.getTwampOriginatorInfo(testdata, 'twamp-light');
    const isReflectorsFromCache = this.verifyReflector(testConfigData);
    if (!isReflectorsFromCache.length) {
      if (twampReflector) {
        twampReflector = JSON.parse(twampReflector);
        twampRefletors = twampReflector['ietf-twamp:twamp']['twamp-client'][
          'twamp-client-ctrl-connection'
        ].map(item => {
          return {
            ipAddress: item['server-ip'],
            type: 'twamp-reflector',
            port: item['server-tcp-port']
          };
        });
      }
      if (twampReflectorLight) {
        twampReflectorLight = JSON.parse(twampReflectorLight);
        twampLightRefletors = twampReflectorLight['ietf-twamp-light:twamp-light'][
          'twamp-light-session-sender'
        ]['test-session'].map(item => {
          return {
            ipAddress: item['reflector-ip'],
            type: 'twamp-light-reflector',
            port: item['reflector-udp-port']
          };
        });
      }
    }

    const allDestinations = [...twampRefletors, ...twampLightRefletors];
    const originatorConfig = {
      initiatorIp: '12.2.2.2',
      subnet: this.getTwampOriginatorInfo(testdata, 'subnet'),
      gatewayIp: this.getTwampOriginatorInfo(testdata, 'gateway'),
      senderUdpPort: 50000,
      isAddressValid: true,
      isGatewayValid: true,
      isSubnetValid: true,
      vlanConfig: vlan && cvlan ? 'q-in-q' : vlan ? 'tagged' : 'untagged',
      etherType: this.getTwampOriginatorInfo(testdata, 'tpid'),
      cVlanPriority: this.getTwampOriginatorInfo(testdata, 'inner-vlan-priority'),
      vlanPriority: this.getTwampOriginatorInfo(testdata, 'vlan-priority'),
      cVlan: cvlan,
      vlan: vlan,
      originatorName: testConfigData.agent.primary.id,
      editMode: true,
      destinations: allDestinations,
      agentIds: this.verifyReflector(testConfigData),
      testName: ''
    };

    return originatorConfig;
  }

  getTwampOriginatorInfo(testdata: Array<any>, id: string): string {
    return testdata.find(item => item.id === id)?.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  verifyReflector(testConfigData: any): any {
    if (!testConfigData?.test) return [];
    const deviceIds =
      testConfigData.test['extended-config'] &&
      testConfigData.test['extended-config']['testPoints'].filter(
        item => item.tag === 'Test Point B' && item?.id
      );
    return deviceIds;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  isCloneDisabled(testData: any): boolean {
    return this.isTestCreatedByInstrument(testData);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  isEditDisable(testData: any): boolean {
    if (this.isTestCreatedByInstrument(testData)) {
      return true;
    } else {
      return testData.getTestType() === TestTypeValue.SERVICE_CONTINUITY ||
        testData.getTestType() === TestTypeValue.VOIP ||
        testData.getTestType() === TestTypeValue.MULTISTEP ||
        testData.getTestType() === TestTypeValue.TWAMP
        ? false
        : ['running', 'scheduled', 'queued', 'starting'].includes(testData.getTestStatus());
    }
  }

  isTestStarted(testData: TestListEntity): boolean {
    return ['running', 'scheduled', 'queued', 'starting'].includes(testData.getTestStatus());
  }

  isTestAborted(testData: TestListEntity): boolean {
    return ['aborted'].includes(testData.getTestStatus());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  isTestCreatedByInstrument(testData: any): boolean {
    let testCreatedByInstrument = false;
    const testListData = testData?.testListData?.['test-list-data'];
    if (typeof testListData === 'string' && testListData !== 'none') {
      const obj = JSON.parse(testListData);
      testCreatedByInstrument = obj?.['createdByInstrument'] === true ? true : false;
    }
    return testCreatedByInstrument;
  }

  private getUpdatedAgentInfo(data: IAgentSelector): IAgentSelector {
    if (data?.id) {
      const deviceData: IEntity = this.devicesService.findById(data.id);
      data.alias = deviceData?.getEscapedName();
    }
    return data;
  }

  private _getDevice$() {
    return this.devicesService.getValues();
  }
  private _getUnManagedDevice$() {
    return this.unmanagedDeviceService.getValues();
  }
}
